import { ref, computed, watch } from 'vue'

/**
 * POS Store Composable
 * Manages cart state, customer selection, and POS-specific operations
 * State persists in sessionStorage to survive page refresh
 */

// Reactive state (shared across all components using this composable)
const cartItems = ref([])
const selectedCustomer = ref(null)
const isPaymentPanelOpen = ref(false)
const selectedCartItemIndex = ref(-1)
const heldTransactions = ref([])
const loyaltySettings = ref(null) // Business loyalty program settings
const applyGST = ref(true) // Per-invoice GST toggle (only for GST businesses)
const businessTaxRegime = ref('non_gst') // Business default tax regime

// Load from sessionStorage on init
const loadFromStorage = () => {
  try {
    const savedCart = sessionStorage.getItem('pos_cart')
    const savedCustomer = sessionStorage.getItem('pos_customer')
    const savedHeld = sessionStorage.getItem('pos_held')

    if (savedCart) cartItems.value = JSON.parse(savedCart)
    if (savedCustomer) selectedCustomer.value = JSON.parse(savedCustomer)
    if (savedHeld) heldTransactions.value = JSON.parse(savedHeld)
  } catch (e) {
    console.error('Failed to load POS state from storage:', e)
  }
}

// Initialize on first import
loadFromStorage()

// Persist to sessionStorage on changes
watch(cartItems, (items) => {
  sessionStorage.setItem('pos_cart', JSON.stringify(items))
}, { deep: true })

watch(selectedCustomer, (customer) => {
  if (customer) {
    sessionStorage.setItem('pos_customer', JSON.stringify(customer))
  } else {
    sessionStorage.removeItem('pos_customer')
  }
})

watch(heldTransactions, (held) => {
  sessionStorage.setItem('pos_held', JSON.stringify(held))
}, { deep: true })

export function usePOSStore() {
  // ==================== COMPUTED ====================

  /**
   * Calculate subtotal — GST-inclusive (prices include tax)
   * This is the total of what customer pays before invoice-level discounts
   */
  const subtotal = computed(() => {
    return cartItems.value.reduce((sum, item) => {
      const lineTotal = item.quantity * item.unit_price
      const discount = item.discount_amount || 0
      return sum + lineTotal - discount
    }, 0)
  })

  /**
   * Calculate tax breakdown — back-calculated from inclusive prices
   * These amounts are already included in the subtotal (informational only)
   * Returns zero taxes if applyGST is false or business is non-GST
   */
  const taxBreakdown = computed(() => {
    if (!applyGST.value || businessTaxRegime.value !== 'gst') {
      return { cgst: 0, sgst: 0, igst: 0, total: 0 }
    }

    let cgst = 0
    let sgst = 0
    let igst = 0

    cartItems.value.forEach((item) => {
      const lineTotal = item.quantity * item.unit_price
      const discount = item.discount_amount || 0
      const inclusiveAmount = lineTotal - discount

      const combinedRate = (item.cgst_rate || 0) + (item.sgst_rate || 0) + (item.igst_rate || 0)
      if (combinedRate <= 0) return

      const taxableAmount = inclusiveAmount / (1 + combinedRate / 100)
      const lineTax = inclusiveAmount - taxableAmount

      if (item.cgst_rate > 0) cgst += lineTax * (item.cgst_rate / combinedRate)
      if (item.sgst_rate > 0) sgst += lineTax * (item.sgst_rate / combinedRate)
      if (item.igst_rate > 0) igst += lineTax * (item.igst_rate / combinedRate)
    })

    return { cgst, sgst, igst, total: cgst + sgst + igst }
  })

  /**
   * Taxable subtotal — inclusive total minus back-calculated tax
   * Used for display: Subtotal (taxable) + CGST + SGST = Grand Total
   */
  const taxableSubtotal = computed(() => {
    return subtotal.value - taxBreakdown.value.total
  })

  /**
   * Total of all per-item discounts (for display in totals breakdown)
   */
  const itemDiscountTotal = computed(() => {
    return cartItems.value.reduce((sum, item) => sum + (item.discount_amount || 0), 0)
  })

  /**
   * Invoice-level discount amount
   */
  const invoiceDiscount = ref(0)
  const invoiceDiscountType = ref(null) // 'percentage' or 'fixed'
  const invoiceDiscountValue = ref(0)

  /**
   * Loyalty points to redeem
   */
  const loyaltyPointsToRedeem = ref(0)
  const loyaltyDiscountAmount = computed(() => {
    // Use rupees_per_point from business loyalty settings, fallback to 0.10
    const rupeesPerPoint = loyaltySettings.value?.rupees_per_point || 0.10
    return loyaltyPointsToRedeem.value * rupeesPerPoint
  })

  /**
   * Calculate grand total — prices are GST-inclusive, so no tax added on top
   */
  const grandTotal = computed(() => {
    const discounts = invoiceDiscount.value + loyaltyDiscountAmount.value
    return Math.max(0, subtotal.value - discounts)
  })

  /**
   * Check if cart has items
   */
  const hasItems = computed(() => cartItems.value.length > 0)

  /**
   * Get cart item count
   */
  const itemCount = computed(() => {
    return cartItems.value.reduce((count, item) => count + item.quantity, 0)
  })

  // ==================== ACTIONS ====================

  /**
   * Add product to cart
   * @param {Object} product - Product object from API
   * @param {number} quantity - Quantity to add (default: 1)
   */
  const addProduct = (product, quantity = 1) => {
    const stock = product.stock_quantity ?? Infinity
    const existingIndex = cartItems.value.findIndex(
      (item) => item.product_id === product.id
    )

    if (existingIndex !== -1) {
      // Cap at available stock
      const newQty = Math.min(cartItems.value[existingIndex].quantity + quantity, stock)
      cartItems.value[existingIndex].quantity = newQty
      cartItems.value[existingIndex].stock_quantity = stock
    } else {
      // Add new item (cap quantity at stock)
      cartItems.value.push({
        product_id: product.id,
        product_name: product.name,
        product_name_local: product.localizations?.ta_IN?.name || product.name,
        sku: product.sku,
        barcode: product.barcode,
        quantity: Math.min(quantity, stock),
        unit_price: parseFloat(product.base_price),
        unit_type: product.unit_type || null,
        hsn_code: product.hsn_code,
        is_taxable: product.is_taxable,
        cgst_rate: product.is_taxable ? (product.cgst_rate ?? 9) : 0,
        sgst_rate: product.is_taxable ? (product.sgst_rate ?? 9) : 0,
        igst_rate: null, // POS is always intra-state (walk-in customers). Backend handles inter-state via invoice API.
        discount_type: 'percentage',
        discount_value: 0,
        discount_amount: 0,
        stock_quantity: stock,
      })
    }

    // Select the newly added/updated item
    const newIndex = existingIndex !== -1 ? existingIndex : cartItems.value.length - 1
    selectedCartItemIndex.value = newIndex
  }

  /**
   * Update quantity of cart item (capped at available stock)
   * @param {number} index - Index in cart
   * @param {number} quantity - New quantity
   * @returns {{ capped: boolean, stock: number }|null} - Returns info if quantity was capped
   */
  const updateQuantity = (index, quantity) => {
    if (index < 0 || index >= cartItems.value.length) return null

    if (quantity <= 0) {
      removeItem(index)
      return null
    }

    const stock = cartItems.value[index].stock_quantity ?? Infinity
    if (quantity > stock) {
      cartItems.value[index].quantity = stock
      return { capped: true, stock }
    }
    cartItems.value[index].quantity = quantity
    return null
  }

  /**
   * Increment quantity of cart item (blocked if at stock limit)
   * @param {number} index - Index in cart
   * @returns {{ capped: boolean, stock: number }|null}
   */
  const incrementQuantity = (index) => {
    if (index < 0 || index >= cartItems.value.length) return null
    const item = cartItems.value[index]
    const stock = item.stock_quantity ?? Infinity
    if (item.quantity >= stock) {
      return { capped: true, stock }
    }
    item.quantity++
    return null
  }

  /**
   * Decrement quantity of cart item
   * @param {number} index - Index in cart
   */
  const decrementQuantity = (index) => {
    if (index < 0 || index >= cartItems.value.length) return
    if (cartItems.value[index].quantity <= 1) {
      removeItem(index)
    } else {
      cartItems.value[index].quantity--
    }
  }

  /**
   * Remove item from cart
   * @param {number} index - Index in cart
   */
  const removeItem = (index) => {
    if (index < 0 || index >= cartItems.value.length) return
    cartItems.value.splice(index, 1)

    // Adjust selected index
    if (selectedCartItemIndex.value >= cartItems.value.length) {
      selectedCartItemIndex.value = cartItems.value.length - 1
    }
  }

  /**
   * Apply discount to cart item
   * @param {number} index - Index in cart
   * @param {string} type - 'percentage' or 'fixed'
   * @param {number} value - Discount value
   */
  const applyItemDiscount = (index, type, value) => {
    if (index < 0 || index >= cartItems.value.length) return

    const item = cartItems.value[index]
    const clampedValue = type === 'percentage' ? Math.min(Math.max(0, value), 100) : Math.max(0, value)
    item.discount_type = type
    item.discount_value = clampedValue

    const lineSubtotal = item.quantity * item.unit_price
    if (type === 'percentage') {
      item.discount_amount = lineSubtotal * (clampedValue / 100)
    } else {
      item.discount_amount = Math.min(clampedValue, lineSubtotal)
    }
  }

  /**
   * Apply invoice-level discount
   * @param {string} type - 'percentage' or 'fixed'
   * @param {number} value - Discount value
   */
  const applyInvoiceDiscount = (type, value) => {
    const clampedValue = type === 'percentage' ? Math.min(Math.max(0, value), 100) : Math.max(0, value)
    invoiceDiscountType.value = type
    invoiceDiscountValue.value = clampedValue

    if (type === 'percentage') {
      invoiceDiscount.value = subtotal.value * (clampedValue / 100)
    } else {
      invoiceDiscount.value = Math.min(clampedValue, subtotal.value)
    }
  }

  /**
   * Set customer
   * @param {Object|null} customer - Customer object or null for walk-in
   */
  const setCustomer = (customer) => {
    selectedCustomer.value = customer
    loyaltyPointsToRedeem.value = 0 // Reset loyalty on customer change
  }

  /**
   * Set loyalty settings from API
   * @param {Object} settings - Loyalty settings from GET /v1/loyalty/settings
   */
  const setLoyaltySettings = (settings) => {
    loyaltySettings.value = settings
  }

  /**
   * Set business tax regime (called when business data is loaded)
   * @param {string} regime - 'gst' or 'non_gst'
   */
  const setBusinessTaxRegime = (regime) => {
    businessTaxRegime.value = regime
    // Default applyGST to true for GST businesses, false otherwise
    applyGST.value = regime === 'gst'
  }

  /**
   * Toggle GST application for current invoice
   * Only allowed for GST-registered businesses
   * @param {boolean} apply - Whether to apply GST
   */
  const setApplyGST = (apply) => {
    if (businessTaxRegime.value === 'gst') {
      applyGST.value = apply
    }
  }

  /**
   * Set loyalty points to redeem
   * @param {number} points - Points to redeem
   */
  const setLoyaltyRedemption = (points) => {
    const maxPoints = selectedCustomer.value?.loyalty_points_balance || 0
    const minPoints = loyaltySettings.value?.min_redeem_points || 0

    // Enforce minimum points threshold
    if (points > 0 && points < minPoints) {
      points = 0 // Don't allow below minimum (user must enter at least minPoints)
    }

    loyaltyPointsToRedeem.value = Math.min(Math.max(0, points), maxPoints)
  }

  /**
   * Update unit price of cart item
   * @param {number} index - Index in cart
   * @param {number} price - New unit price
   */
  const updateUnitPrice = (index, price) => {
    if (index < 0 || index >= cartItems.value.length) return
    const item = cartItems.value[index]
    item.unit_price = Math.max(0, price)

    // Recalculate discount amount if a discount is applied
    if (item.discount_type && item.discount_value > 0) {
      const lineSubtotal = item.quantity * item.unit_price
      if (item.discount_type === 'percentage') {
        item.discount_amount = lineSubtotal * (item.discount_value / 100)
      } else {
        item.discount_amount = Math.min(item.discount_value, lineSubtotal)
      }
    }
  }

  /**
   * Clear cart and reset state
   */
  const clearCart = () => {
    cartItems.value = []
    selectedCustomer.value = null
    selectedCartItemIndex.value = -1
    invoiceDiscount.value = 0
    invoiceDiscountType.value = null
    invoiceDiscountValue.value = 0
    loyaltyPointsToRedeem.value = 0
    isPaymentPanelOpen.value = false
    // Reset GST toggle to default (true for GST businesses)
    applyGST.value = businessTaxRegime.value === 'gst'

    // Clear sessionStorage
    sessionStorage.removeItem('pos_cart')
    sessionStorage.removeItem('pos_customer')
  }

  /**
   * Hold current transaction
   * @returns {number} Index of held transaction
   */
  const holdTransaction = () => {
    if (!hasItems.value) return -1

    const held = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      customer: selectedCustomer.value,
      items: [...cartItems.value],
      subtotal: subtotal.value,
      total: grandTotal.value,
    }

    heldTransactions.value.push(held)
    clearCart()

    return heldTransactions.value.length - 1
  }

  /**
   * Recall held transaction
   * @param {number} index - Index in held transactions
   */
  const recallTransaction = (index) => {
    if (index < 0 || index >= heldTransactions.value.length) return

    const held = heldTransactions.value[index]

    // If current cart has items, hold it first
    if (hasItems.value) {
      holdTransaction()
    }

    // Restore held transaction
    cartItems.value = [...held.items]
    selectedCustomer.value = held.customer

    // Remove from held list
    heldTransactions.value.splice(index, 1)
  }

  /**
   * Delete held transaction
   * @param {number} index - Index in held transactions
   */
  const deleteHeldTransaction = (index) => {
    if (index < 0 || index >= heldTransactions.value.length) return
    heldTransactions.value.splice(index, 1)
  }

  /**
   * Build invoice data for API
   * @returns {Object} Invoice data ready for createInvoice API
   */
  const buildInvoiceData = () => {
    // Only include apply_gst if business is GST-registered and user toggled it off
    const applyGSTFlag = businessTaxRegime.value === 'gst' ? applyGST.value : null

    return {
      customer_id: selectedCustomer.value?.id || null,
      payment_mode: 'cash', // Will be set during payment
      apply_gst: applyGSTFlag, // Per-invoice GST toggle
      items: cartItems.value.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cgst_rate: applyGST.value && item.cgst_rate > 0 ? item.cgst_rate : null,
        sgst_rate: applyGST.value && item.sgst_rate > 0 ? item.sgst_rate : null,
        igst_rate: applyGST.value && item.igst_rate > 0 ? item.igst_rate : null,
        discount_type: item.discount_type,
        discount_value: item.discount_value,
      })),
      discount_type: invoiceDiscountType.value,
      discount_value: invoiceDiscountValue.value,
      loyalty_points_to_redeem: loyaltyPointsToRedeem.value > 0 ? loyaltyPointsToRedeem.value : 0,
    }
  }

  /**
   * Open/close payment panel
   */
  const openPaymentPanel = () => {
    if (hasItems.value) {
      isPaymentPanelOpen.value = true
    }
  }

  const closePaymentPanel = () => {
    isPaymentPanelOpen.value = false
  }

  /**
   * Select cart item by index
   */
  const selectCartItem = (index) => {
    if (index >= -1 && index < cartItems.value.length) {
      selectedCartItemIndex.value = index
    }
  }

  /**
   * Move selection up/down in cart
   */
  const selectPreviousItem = () => {
    if (selectedCartItemIndex.value > 0) {
      selectedCartItemIndex.value--
    }
  }

  const selectNextItem = () => {
    if (selectedCartItemIndex.value < cartItems.value.length - 1) {
      selectedCartItemIndex.value++
    }
  }

  return {
    // State
    cartItems,
    selectedCustomer,
    selectedCartItemIndex,
    isPaymentPanelOpen,
    heldTransactions,
    loyaltyPointsToRedeem,
    loyaltySettings,
    applyGST,
    businessTaxRegime,

    // Computed
    subtotal,
    taxableSubtotal,
    taxBreakdown,
    invoiceDiscount,
    itemDiscountTotal,
    loyaltyDiscountAmount,
    grandTotal,
    hasItems,
    itemCount,

    // Actions
    addProduct,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    removeItem,
    applyItemDiscount,
    applyInvoiceDiscount,
    updateUnitPrice,
    setCustomer,
    setLoyaltySettings,
    setLoyaltyRedemption,
    setBusinessTaxRegime,
    setApplyGST,
    clearCart,
    holdTransaction,
    recallTransaction,
    deleteHeldTransaction,
    buildInvoiceData,
    openPaymentPanel,
    closePaymentPanel,
    selectCartItem,
    selectPreviousItem,
    selectNextItem,
  }
}
